import { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, MapPin } from "lucide-react";
import { CookieSettingsDialog } from "@/components/CookieSettingsDialog";

import logo from "@/assets/logo.png";
import { ObfuscatedEmail } from "@/components/ObfuscatedEmail";

const footerLinks = {
  leistungen: [
    { name: "Fulfillment", href: "/fulfillment" },
    { name: "Pre-FBA Service", href: "/pre-fba" },
    { name: "Seller Fulfilled Prime", href: "/seller-fulfilled-prime" },
    { name: "Lohnverpackung", href: "/lohnverpackung" },
    { name: "Aktionsversand", href: "/aktionsversand" },
    { name: "Retourenmanagement", href: "/retouren" },
    { name: "Fulfillment Dashboard", href: "/fulfillment-dashboard" },
  ],
  unternehmen: [
    { name: "Über uns", href: "/ueber-uns" },
    { name: "greenmoose", href: "/greenmoose" },
    { name: "Qualitätsversprechen", href: "/qualitaetsversprechen" },
    { name: "Unser Fairplay", href: "/fairplay" },
    { name: "Karriere", href: "/karriere" },
    { name: "Blog", href: "/blog" },
    { name: "Podcast", href: "/podcast" },
  ],
  service: [
    { name: "FAQ", href: "/faq" },
    { name: "Branchen", href: "/branchen" },
    { name: "Schnittstellen", href: "/schnittstellen" },
    { name: "Anbieter wechseln", href: "/fulfillment-wechseln" },
    { name: "So startest du", href: "/onboarding" },
    { name: "Unsere Tools", href: "/tools" },
    { name: "Kontakt", href: "/kontakt" },
  ],
  rechtliches: [] as { name: string; href: string; external?: boolean }[],
};

export function Footer() {
  const [cookieSettingsOpen, setCookieSettingsOpen] = useState(false);
  

  return (
    <footer className="bg-foreground text-background">
      <div className="container-section pt-8 pb-4 lg:pt-10 lg:pb-5">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <img src={logo} alt="emoose fulfillment" width={581} height={126} className="h-12 sm:h-14 w-auto brightness-0 invert mb-6" />
            <p className="text-background/70 text-sm leading-relaxed mb-6 max-w-sm">
              Professionelle Fulfillment- und Versandlösungen für E-Commerce-Händler. 
              Wir kümmern uns um deine Logistik, damit du dich auf dein Geschäft konzentrieren kannst.
            </p>
            <div className="space-y-3">
              <ObfuscatedEmail className="flex items-center gap-3 text-sm text-background/70 hover:text-background transition-colors">
                <Mail className="h-4 w-4" />
                <span>info<span className="hidden" aria-hidden="true">REMOVE</span>@<span className="hidden" aria-hidden="true">REMOVE</span>emoose.de</span>
              </ObfuscatedEmail>
              <a href="tel:+4968198573774" className="flex items-center gap-3 text-sm text-background/70 hover:text-background transition-colors">
                <Phone className="h-4 w-4" />
                0681 98573774
              </a>
              <div className="flex items-start gap-3 text-sm text-background/70">
                <MapPin className="h-4 w-4 mt-0.5" />
                <span>In den Hallen 9/13, 66115 Saarbrücken</span>
              </div>
            </div>
          </div>

          {/* Links Columns */}
          <div>
            <h3 className="font-display font-semibold text-sm uppercase tracking-wider mb-4">Leistungen</h3>
            <ul className="space-y-3">
              {footerLinks.leistungen.map((link) => (
                <li key={link.href}>
                  <Link to={link.href} className="text-sm text-background/70 hover:text-background transition-colors">
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>


          <div>
            <h3 className="font-display font-semibold text-sm uppercase tracking-wider mb-4">Unternehmen</h3>
            <ul className="space-y-3">
              {footerLinks.unternehmen.map((link) => (
                <li key={link.href}>
                  <Link to={link.href} className="text-sm text-background/70 hover:text-background transition-colors">
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="font-display font-semibold text-sm uppercase tracking-wider mb-4">Service</h3>
            <ul className="space-y-3">
              {footerLinks.service.map((link) => (
                <li key={link.href}>
                  <Link to={link.href} className="text-sm text-background/70 hover:text-background transition-colors">
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-background/10 mt-12 pt-4 flex flex-col sm:flex-row sm:flex-wrap justify-between items-start sm:items-center gap-4">
          <p className="text-sm text-background/50">
            © {new Date().getFullYear()} emoose GmbH. Alle Rechte vorbehalten.
          </p>
          <div className="flex gap-6 flex-wrap">
            <a href="https://stats.uptimerobot.com/4BD9k0OHbn/" target="_blank" rel="noopener noreferrer" className="text-sm text-background/50 hover:text-background transition-colors">
              Status
            </a>
            <a href="https://fulfillment-dashboard.emoose.de" target="_blank" rel="noopener noreferrer" className="text-sm text-background/50 hover:text-background transition-colors">
              Kunden-Login
            </a>
            <button
              onClick={() => setCookieSettingsOpen(true)}
              className="text-sm text-background/50 hover:text-background transition-colors"
            >
              Cookies
            </button>
            <button
              onClick={() => window.dispatchEvent(new CustomEvent('open-accessibility'))}
              className="text-sm text-background/50 hover:text-background transition-colors"
            >
              Barrierefreiheit
            </button>
          </div>
          <div className="flex gap-6 flex-wrap">
            <Link to="/impressum" className="text-sm text-background/50 hover:text-background transition-colors">
              Impressum
            </Link>
            <Link to="/datenschutz" className="text-sm text-background/50 hover:text-background transition-colors">
              Datenschutz
            </Link>
            <Link to="/barrierefreiheit" className="text-sm text-background/50 hover:text-background transition-colors">
              Erklärung zur Barrierefreiheit
            </Link>
          </div>
          <CookieSettingsDialog
            open={cookieSettingsOpen}
            onOpenChange={setCookieSettingsOpen}
          />
        </div>
      </div>
    </footer>
  );
}
